import React, { type FC, type PropsWithChildren } from 'react';

import { useSiteMetadata } from '@lucid/hooks';
import kebabCase from 'lodash/kebabCase';

import type { Lucid } from '../../types/types';

export type SEOProps = {
  metatags?: Lucid.Context.Metatag[];
  schemaMarkup?: string;
  location?: Partial<Location>;
  shortCode?: Lucid.I18n.LanguagesShortCode;
  product: Lucid.Product.Sites;
};

const favicons: Partial<Record<Lucid.Product.Sites, string>> = {
  co: 'https://cdn-cashy-static-assets.lucid.co/marketing/images/LucidSoftwareFavicon.png',
  chart: 'https://cdn-cashy-static-assets.lucidchart.com/marketing/images/Lucidchart_favicon_full_96x96.png',
  spark: 'https://cdn-cashy-static-assets.lucidspark.com/marketing/images/Lucidspark_favicon_full_96x96.png',
  scale: 'https://cdn-cashy-static-assets.lucidscale.com/marketing/images/Lucidscale_favicon_full_96x96.png',
  edu: 'https://cdn-cashy-static-assets.lucidchart.com/marketing/images/LucidSoftwareFavicon.png',
  chartblog: 'https://cdn-cashy-static-assets.lucidchart.com/marketing/images/Lucidchart_favicon_full_96x96.png',
};

const SEO: FC<PropsWithChildren<SEOProps>> = ({
  children,
  metatags = [],
  schemaMarkup = '',
  location,
  shortCode = 'en',
  product,
}) => {
  const { siteUrl, title } = useSiteMetadata();
  const pathname = location?.pathname;
  const shouldOverrideCanonical = !!metatags.find(tag => tag.key === 'canonical');
  const hasTitle = !!metatags.find(tag => tag.key === 'title');
  const tagsWithName = ['description', 'keywords', 'robots'];
  const currentMetatags = metatags.reduce<Lucid.Context.Metatag[]>((previous, metatag) => {
    if (!metatag.key || !metatag.value) return previous;

    return [...previous, metatag];
  }, []);

  return (
    <React.Fragment key="seo-html">
      <html id={`html-lang-${shortCode}`} lang={shortCode} />
      {!hasTitle && (
        <title key={`base-title-key-${kebabCase(title)}`} id="base-title">
          {title}
        </title>
      )}
      <link key="seo-shortcut-icon" href={favicons[product]} id="shortcut-icon" rel="shortcut icon" type="image/png" />
      {currentMetatags?.length &&
        currentMetatags.map(({ key, value }, index) => {
          if (!key || !value || (!key && !value)) return undefined;

          if (key === 'title') {
            return (
              <title key={`title-key-${kebabCase(value)}-${index}`} id={key}>
                {value}
              </title>
            );
          } else if (key === 'canonical') {
            return <link key={`canonical-${kebabCase(value)}-${index}`} href={value} id={key} rel={key} />;
          } else if (tagsWithName.includes(key)) {
            return <meta key={`${kebabCase(value)}-${index}`} content={value} id={key} name={key} />;
          } else {
            return <meta key={`${kebabCase(value)}-${index}`} content={value} id={key} property={key} />;
          }
        })}
      {!shouldOverrideCanonical && pathname && (
        <link
          key={`canonical-override-${siteUrl}${pathname}`}
          href={`${siteUrl}${pathname}`}
          id="canonical"
          rel="canonical"
        />
      )}
      {schemaMarkup && (
        <script key="schema-structured-data" id="schema-structured-data" type="application/ld+json">
          {schemaMarkup}
        </script>
      )}
      {children}
    </React.Fragment>
  );
};

export { SEO };
export default SEO;
